import React, { useEffect, useRef, useState } from "react";
import { useDispatch, useSelector } from "react-redux";
import { useLocation, Redirect } from "react-router-dom";
import { checkSubscriptionStatus } from "../../actions/subscriptionActions";
import {
  selectHasActiveSubscription,
  selectIsCheckingStatus,
} from "../../reducers/subscription";
import SubscriptionPrompt from "./SubscriptionPrompt";

/**
 * Global subscription gate.
 *
 * Every authenticated user must have an active subscription. If they don't,
 * any path under /:lang/app/* is replaced with the SubscriptionPrompt — they
 * cannot navigate around it. Public pages (login, register, /:lang/prices,
 * etc.) are still allowed through so users can sign up / pay.
 */
const APP_PATH_RE = /^\/[^/]+\/app(\/|$)/;

const AppSubscriptionWrapper = ({ children }) => {
  const dispatch = useDispatch();
  const location = useLocation();
  const hasActiveSubscription = useSelector(selectHasActiveSubscription);
  const isCheckingStatus = useSelector(selectIsCheckingStatus);

  // Read profile lazily — but use a STABLE primitive (userId) for effect deps,
  // otherwise the parsed object gets a new reference each render → infinite loop.
  const userId = (() => {
    try {
      const profile = JSON.parse(localStorage.getItem("profile"));
      return profile?.result?._id || profile?.result?.googleId || null;
    } catch (_) {
      return null;
    }
  })();

  const [checkAttempted, setCheckAttempted] = useState(false);
  const calledForUserRef = useRef(null);

  // Verify once per logged-in user. The ref guards against React StrictMode
  // double-invocation and any unexpected remount/re-render — the API is hit
  // at most one time per userId per process lifetime.
  useEffect(() => {
    if (!userId) return;
    if (calledForUserRef.current === userId) return;
    calledForUserRef.current = userId;

    let cancelled = false;
    (async () => {
      try {
        await dispatch(checkSubscriptionStatus());
      } finally {
        if (!cancelled) setCheckAttempted(true);
      }
    })();

    return () => {
      cancelled = true;
    };
  }, [dispatch, userId]);

  const isAppPath = APP_PATH_RE.test(location.pathname);

  // Not logged in + trying to access /app/* → bounce to login (root /:lang/).
  if (!userId && isAppPath) {
    const lang = location.pathname.split("/")[1] || "en";
    return <Redirect to={`/${lang}/`} />;
  }

  // Only gate after the verification API returned and confirmed no subscription.
  const needsSubscription =
    userId &&
    isAppPath &&
    checkAttempted &&
    !isCheckingStatus &&
    hasActiveSubscription === false;

  if (needsSubscription) {
    return <SubscriptionPrompt />;
  }

  return children;
};

export default AppSubscriptionWrapper;
